import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/AuthProvider';
import { supabase } from '@/lib/supabase';
import { workRpc } from '@/features/workMode/api';
import type { ScrappedPart, WorkDefect, WorkDefectSeverity, WorkDefectStatus } from '@/features/workMode/types';
import type { ScrapAlert, ScrapLogSummary } from './types';

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function asNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = asNumber(value, Number.NaN);
  return Number.isFinite(n) ? n : null;
}

function normalizeDefect(row: WorkDefect): WorkDefect {
  return {
    ...row,
    scrap_tags: Array.isArray(row.scrap_tags) ? row.scrap_tags : [],
    needs_rework: Boolean(row.needs_rework),
    rework_notes: row.rework_notes ?? null,
    committed_seconds: asNumberOrNull(row.committed_seconds),
    material_cost_cents: asNumberOrNull(row.material_cost_cents),
    labor_cost_cents: asNumberOrNull(row.labor_cost_cents),
    scrapped_parts: Array.isArray(row.scrapped_parts) ? row.scrapped_parts : [],
    photo_paths: Array.isArray(row.photo_paths) ? row.photo_paths : [],
  };
}

function normalizeSummary(raw: ScrapLogSummary): ScrapLogSummary {
  const employee = raw.top_employee;
  const part = raw.top_part;
  const tag = raw.top_tag;
  const component = raw.top_component;
  return {
    window_days: asNumber(raw.window_days, 30),
    good_units: asNumber(raw.good_units),
    scrap_units: asNumber(raw.scrap_units),
    scrap_rate: asNumberOrNull(raw.scrap_rate),
    material_cents: asNumber(raw.material_cents),
    labor_cents: asNumber(raw.labor_cents),
    total_cents: asNumber(raw.total_cents),
    committed_seconds: asNumber(raw.committed_seconds),
    rework_count: asNumber(raw.rework_count),
    scrap_logs: asNumber(raw.scrap_logs),
    labor_missing: asNumber(raw.labor_missing),
    uncosted_component_lines: asNumber(raw.uncosted_component_lines),
    top_employee: employee
      ? { user_id: employee.user_id, scrap_units: asNumber(employee.scrap_units), cents: asNumber(employee.cents) }
      : null,
    top_part: part
      ? { item_id: part.item_id, scrap_units: asNumber(part.scrap_units), cents: asNumber(part.cents) }
      : null,
    top_tag: tag ? { tag: tag.tag, count: asNumber(tag.count) } : null,
    top_component: component
      ? {
          key: component.key,
          name: component.name,
          sku: component.sku ?? null,
          qty: asNumber(component.qty),
          events: asNumber(component.events),
          cents: asNumber(component.cents),
        }
      : null,
  };
}

function normalizeAlert(row: ScrapAlert): ScrapAlert {
  return {
    ...row,
    scrap_count: asNumber(row.scrap_count),
    sample_count: asNumber(row.sample_count),
    scrap_rate: asNumber(row.scrap_rate),
    baseline_rate: asNumber(row.baseline_rate),
    z_score: asNumber(row.z_score),
    dollar_cents: asNumber(row.dollar_cents),
    window_days: asNumber(row.window_days, 30),
  };
}

export function scrapLogCents(row: Pick<WorkDefect, 'material_cost_cents' | 'labor_cost_cents'>): number {
  return (row.material_cost_cents ?? 0) + (row.labor_cost_cents ?? 0);
}

export function useScrapLogs() {
  return useQuery({
    queryKey: ['scrap', 'logs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_defects' as never)
        .select('*')
        .eq('disposition', 'scrap')
        .order('created_at', { ascending: false })
        .limit(400);
      if (error) throw error;
      return ((data as unknown as WorkDefect[]) ?? []).map(normalizeDefect);
    },
  });
}

export function useScrapSummary(days = 30) {
  return useQuery({
    queryKey: ['scrap', 'summary', days],
    queryFn: async () => normalizeSummary(await workRpc<ScrapLogSummary>('scrap_log_summary', { p_days: days })),
  });
}

/** Recompute anomalies, then return the ones still worth acting on. */
export function useScrapAlerts(days = 30) {
  return useQuery({
    queryKey: ['scrap', 'alerts', days],
    queryFn: async () => {
      try {
        await workRpc<number>('scrap_refresh_alerts', { p_days: days });
      } catch {
        // A failed refresh still leaves the last stored alerts readable.
      }
      const { data, error } = await supabase
        .from('scrap_alerts' as never)
        .select('*')
        .in('status', ['open', 'acknowledged'])
        .order('z_score', { ascending: false });
      if (error) throw error;
      const rows = ((data as unknown as ScrapAlert[]) ?? []).map(normalizeAlert);
      return rows.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
        return b.z_score - a.z_score;
      });
    },
  });
}

export interface ScrapLogUpdate {
  id: string;
  description: string;
  severity: WorkDefectSeverity;
  scrapTags: string[];
  needsRework: boolean;
  reworkNotes: string | null;
  scrappedParts: ScrappedPart[];
  status: WorkDefectStatus;
  reviewNotes: string | null;
}

export function useUpdateScrapLog() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: ScrapLogUpdate) => {
      const { data, error } = await supabase
        .from('work_defects' as never)
        .update({
          description: input.description.trim(),
          severity: input.severity,
          scrap_tags: input.scrapTags,
          needs_rework: input.needsRework,
          rework_notes: input.needsRework ? input.reworkNotes : null,
          scrapped_parts: input.scrappedParts,
          status: input.status,
          review_notes: input.reviewNotes,
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        } as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      try {
        await workRpc<number>('scrap_refresh_alerts', { p_days: 30 });
      } catch {
        /* the edited row is saved; alerts catch up on the next open */
      }
      return normalizeDefect(data as unknown as WorkDefect);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scrap'] });
      void qc.invalidateQueries({ queryKey: ['work', 'defects'] });
    },
  });
}

export interface ScrapLogCreate {
  description: string;
  scrapTags: string[];
  itemId: string;
  userId: string;
  workstationId: string | null;
  severity: WorkDefectSeverity;
  needsRework: boolean;
  reworkNotes: string | null;
  scrappedParts: ScrappedPart[];
  photoPaths: string[];
  committedSeconds: number | null;
  progressPct: number | null;
}

/** Office entry: one scrapped unit, written by `scrap_log_create`. */
export function useCreateScrapLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScrapLogCreate) => {
      const row = await workRpc<WorkDefect>('scrap_log_create', {
        p_description: input.description.trim(),
        p_scrap_tags: input.scrapTags,
        p_item_id: input.itemId,
        p_user_id: input.userId,
        p_workstation_id: input.workstationId,
        p_severity: input.severity,
        p_needs_rework: input.needsRework,
        p_rework_notes: input.needsRework ? input.reworkNotes : null,
        p_scrapped_parts: input.scrappedParts,
        p_photo_paths: input.photoPaths,
        p_committed_seconds: input.committedSeconds,
        p_progress_pct: input.progressPct,
      });
      return normalizeDefect(row);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['scrap'] });
      void qc.invalidateQueries({ queryKey: ['work', 'defects'] });
      void qc.invalidateQueries({ queryKey: ['work', 'units'] });
      void qc.invalidateQueries({ queryKey: ['work', 'stats'] });
      void qc.invalidateQueries({ queryKey: ['work', 'leaderboard'] });
      void qc.invalidateQueries({ queryKey: ['work', 'station-daily'] });
    },
  });
}

export function useAcknowledgeScrapAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workRpc<ScrapAlert>('scrap_alert_set_status', { p_id: id, p_status: 'acknowledged' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scrap', 'alerts'] }),
  });
}
